import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';
import './Pill.css';

/**
 * Pill — ported from the reporting UI, same API and the same styling values
 * (see Pill.css). No MUI, no Tailwind: plain CSS on the reporting design
 * tokens in tokens.css.
 *
 * Props
 *   variant  — 'primary' (default) | 'light' | 'ghost' | 'square'
 *   size     — 'xs' | 'sm' | 'md' (default) | 'lg' | 'xl' | '2xl' | '3xl'
 *   onClick  — makes the pill a real <button>; without it, a <span>
 *   removable / onRemove — adds the trailing X; its click does not reach onClick
 *   removeLabel — verb used in the X's tooltip/aria-label (default 'Remove')
 *   label    — convenience alias for `children`, for single-word pills
 *
 * Selection is the caller's job, exactly as in reporting's PillList:
 * pass variant="primary" when selected and variant="light" when not.
 */
const Pill = React.forwardRef(function Pill({
  className,
  variant = 'primary',
  size = 'md',
  children,
  label,
  removable = false,
  onRemove,
  onClick,
  removeLabel = 'Remove',
  ...props
}, ref) {
  const content = children ?? label;

  const handleRemove = (event) => {
    // Without this the pill's own onClick fires too and undoes the removal.
    event.stopPropagation();
    onRemove?.();
  };

  // A removable pill already contains a <button> for the X, and a <button> inside a
  // <button> is invalid HTML. So a pill that is both clickable and removable becomes
  // a span carrying the button role and keyboard handling instead — the same shape
  // MUI's deletable Chip uses. Click-only pills stay the plain <button> reporting uses.
  const isButton = !!onClick && !removable;
  const Component = onClick ? (isButton ? 'button' : 'span') : 'span';

  const keyboardProps = onClick && !isButton
    ? {
      role: 'button',
      tabIndex: 0,
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(event);
        }
      },
    }
    : {};

  return (
    <Component
      ref={ref}
      className={cn('pill', `pill--${variant}`, `pill--${size}`, onClick && 'pill--clickable', className)}
      onClick={onClick}
      {...(isButton ? { type: 'button' } : {})}
      {...keyboardProps}
      {...props}
    >
      <span className="pill__label">{content}</span>
      {removable && (
        <button
          type="button"
          onClick={handleRemove}
          className="pill__remove"
          aria-label={`${removeLabel} ${content}`}
          title={`${removeLabel} ${content}`}
        >
          <X className="pill__remove-icon" />
        </button>
      )}
    </Component>
  );
});

export default Pill;
